import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// The familiar-greeting pool (BIZY-683). Keys under greeting.familiarVariants.
// `readyWhenYouAre` stays first so tests pinning Math.random() = 0 keep the
// canonical copy. The nameless set doubles as the fallback when the surface
// has no display name.
const NAMED_VARIANT_KEYS = [
	'readyWhenYouAre',
	'moreWhereThatCameFrom',
	'stillHere',
	'returns',
	'letsBoogie',
	'backSoSoon',
	'ohHey',
	'floorIsYours',
	'rejoinsTheParty',
] as const;
const NAMELESS_VARIANT_KEYS = ['andWereBack', 'missMe', 'youAgain', 'neverLeft'] as const;

/**
 * A randomly chosen familiar greeting ("Hey JT, ready when you are." /
 * "JT returns!" / "Miss me?" …), shared by the web side panel's empty state +
 * blank slate and Bizy Desktop's blank slate. The pick is stable across
 * re-renders and re-rolls when `refreshKey` changes, so each fresh showing can
 * greet differently without the copy shuffling mid-view.
 */
export function useBizyFamiliarGreeting(displayName: string | undefined, refreshKey: unknown = true): string {
	const { t } = useTranslation('admin_bizy');
	const [roll, setRoll] = useState(() => Math.random());
	useEffect(() => {
		setRoll(Math.random());
	}, [refreshKey]);

	const variantKeys: readonly string[] = displayName ? [...NAMED_VARIANT_KEYS, ...NAMELESS_VARIANT_KEYS] : NAMELESS_VARIANT_KEYS;
	const variantKey = variantKeys[Math.floor(roll * variantKeys.length) % variantKeys.length];
	return t(`greeting.familiarVariants.${variantKey}`, displayName ? { name: displayName } : undefined);
}
